/**
 * useImageCrop - Hook to toggle and persist cropped variant preference
 *
 * Updates the image_url in Supabase to switch between:
 * - Original: https://imagedelivery.net/{hash}/{id}/public
 * - Cropped:  https://imagedelivery.net/{hash}/{id}/marketing
 *
 * The "marketing" variant is configured in Cloudflare Images to crop
 * the bottom portion of images where watermarks typically appear.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { getCroppedImageUrl, getOriginalImageUrl, isCroppedVariant } from '@/lib/cloudflareImages'
import { useToast } from '@/hooks/use-toast'

interface UseImageCropOptions {
  /** The image ID in franchise_image_jobs table */
  imageId: number | string
  /** Current image URL */
  imageUrl: string
  /** Callback after successful update */
  onSuccess?: (newUrl: string) => void
}

export function useImageCrop({ imageId, imageUrl, onSuccess }: UseImageCropOptions) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Determine current state from URL
  const isCropped = isCroppedVariant(imageUrl)

  const mutation = useMutation({
    mutationFn: async (crop: boolean) => {
      const newUrl = crop ? getCroppedImageUrl(imageUrl) : getOriginalImageUrl(imageUrl)

      const { error } = await supabase
        .from('franchise_image_jobs')
        .update({ image_url: newUrl })
        .eq('id', imageId)

      if (error) throw error

      return newUrl
    },
    onSuccess: (newUrl) => {
      // Invalidate queries that might show this image
      queryClient.invalidateQueries({ queryKey: ['gallery'] })
      queryClient.invalidateQueries({ queryKey: ['approval'] })
      queryClient.invalidateQueries({ queryKey: ['images'] })

      toast({
        title: isCropped ? 'Restored original' : 'Cropped watermark',
        description: isCropped
          ? 'Image URL reset to original version'
          : 'Image URL updated to cropped version',
      })

      onSuccess?.(newUrl)
    },
    onError: (error) => {
      console.error('Failed to update image URL:', error)
      toast({
        title: 'Failed to update image',
        description: 'Could not save crop preference. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const toggleCrop = () => {
    mutation.mutate(!isCropped)
  }

  const setCropped = (crop: boolean) => {
    if (crop !== isCropped) {
      mutation.mutate(crop)
    }
  }

  return {
    isCropped,
    toggleCrop,
    setCropped,
    isUpdating: mutation.isPending,
  }
}
